import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import {
  fetchJointDecisionsSpreadsheetCharacteristics,
  fetchPatientById,
  fetchRecommendationById,
  fetchStudyChartFacts,
} from '../../api';
import {
  ARMS,
  REGIONS,
  buildOutline,
  completedSubsectionKeys,
  criteriaProgress,
  criteriaQuestionEntries,
  findLocation,
  formProgress,
  firstLocation,
  instanceKey,
  isFirstFormDate,
  loadCompassFields,
  outlineStops,
  applySharedFormDate,
  splitCaseFields,
  visibleFieldEntries,
} from '../../compass/compassModel';
import { prefillCompassChart } from '../../compass/prefillFromPatient';
import { stampPatientDemographics } from '../../studies/applyChartFacts';
import { phaseFlagsForOutline } from '../../studies/studyPipeline';
import {
  adoptStudy,
  broadcastStudy,
  clearExampleChart,
  commitLinkedEdit,
  demoChartsUseDatabase,
  flushDemoChart,
  flushOpenDemoCharts,
  hydrateDemoChart,
  loadStudy,
  overlaySavedAnswers,
  patientChartId,
  persistDemoStudy,
  saveStudy,
  savedDemographicsBlank,
  studyWasOpened,
} from '../../studies/studyChartStore';
import CompassForm, { FormNavbar } from './CompassForm';
import CriteriaPanel from './CriteriaPanel';
import { useWhatsNext } from './useWhatsNext';
import LoadingSpinner from '../LoadingSpinner';
import './compass.css';

function emptyState() {
  return {
    arm: 'Cervical',
    region: '',
    active: null,
    forms: {},
    visits: {},
  };
}

function chartIsReady(patient, characteristics, chartFacts) {
  if (characteristics && !characteristics.error && characteristics.patient_profile) return true;
  if (chartFacts?.demographics) return true;
  const phi = patient?.phi;
  return Boolean(phi && (phi.Sex || phi.sex || phi.Ethnicity || phi.ethnicity || phi.DOB || phi.dob || phi.Race || phi.race));
}

function savedState(demo, patientId) {
  const saved = loadStudy(patientChartId(demo, patientId), 'compass');
  if (!saved || !ARMS.some((arm) => arm.id === saved.arm)) return null;
  return { ...emptyState(), ...saved, forms: saved.forms || {}, visits: saved.visits || {} };
}

function formValues(state, key) {
  return state.forms[key]?.values || {};
}

export default function CompassWorkspace({
  demo = false,
  patient = null,
  patients = [],
  onSelectPatient,
  embedded = false,
  onEngage,
}) {
  const [fields, setFields] = useState(null);
  const [error, setError] = useState('');
  const [chartNote, setChartNote] = useState(() => {
    if (!demo) return 'example';
    return patient?.id ? 'loading' : 'pick';
  });
  const [chartPatientId, setChartPatientId] = useState(patient?.id ?? null);
  if ((patient?.id ?? null) !== chartPatientId) {
    setChartPatientId(patient?.id ?? null);
    if (demo) setChartNote(patient?.id ? 'loading' : 'pick');
  }
  const [state, setState] = useState(() => (demo ? emptyState() : (savedState(false, null) || emptyState())));
  const [revealed, setRevealed] = useState({});
  const [dateOffer, setDateOffer] = useState(null);
  const dateDeclinedRef = useRef(false);
  const sources = useRef(new Map());
  const chartIdRef = useRef('');
  const stateRef = useRef(state);
  const ownerRef = useRef(null);
  const patientRef = useRef(patient);
  stateRef.current = state;
  patientRef.current = patient;
  chartIdRef.current = patientChartId(demo, patient?.id);

  useEffect(() => {
    let cancelled = false;
    const url = `${process.env.PUBLIC_URL || ''}/compass_patient_fields.csv`;
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('The COMPASS field list did not load.');
        return response.text();
      })
      .then((text) => {
        if (!cancelled) setFields(loadCompassFields(text));
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message || 'The COMPASS field list did not load.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setRevealed({});
  }, [patient?.id]);

  useEffect(() => {
    if (!demo) {
      ownerRef.current = null;
      setChartNote('example');
      setState(adoptStudy('example', 'compass', savedState(false, null) || emptyState()));
      return undefined;
    }
    const id = patient?.id;
    if (ownerRef.current && ownerRef.current !== id) {
      saveStudy(patientChartId(true, ownerRef.current), 'compass', stateRef.current);
      flushDemoChart(ownerRef.current);
    }
    if (!id) {
      ownerRef.current = null;
      setChartNote('pick');
      setState(emptyState());
      return undefined;
    }

    let cancel = false;
    ownerRef.current = id;
    setChartNote('loading');
    (async () => {
      try {
        await hydrateDemoChart(id);
      } catch (loadError) {
        // The form still opens from Phoenix when the saved chart cannot be read.
      }
      if (cancel || ownerRef.current !== id) return;
    const chartId = patientChartId(true, id);
    const saved = savedState(true, id);
    const seeded = stampPatientDemographics(saved || emptyState(), patientRef.current || patient, 'compass');
    const ethnicity = seeded.forms?.['demographics::pre-op']?.values?.ethnicity;
    if (saved && studyWasOpened(chartId, 'compass') && ethnicity && !savedDemographicsBlank(seeded, 'compass')) {
      setState(adoptStudy(chartId, 'compass', seeded));
      setChartNote('ready');
      return;
    }

    setState(seeded);
      let current = patientRef.current || patient || {};
      try {
        const loaded = await fetchPatientById(id);
        if (loaded && typeof loaded === 'object') current = { ...current, ...loaded };
      } catch (loadError) {
        current = patientRef.current || patient || {};
      }
      let characteristics = null;
      let recommendation = null;
      try {
        characteristics = await fetchJointDecisionsSpreadsheetCharacteristics(id);
      } catch (loadError) {
        characteristics = null;
      }
      if (current.primary_recommendation_id) {
        try {
          recommendation = await fetchRecommendationById(id, current.primary_recommendation_id);
        } catch (loadError) {
          recommendation = null;
        }
      }
      let chartFacts = null;
      try {
        chartFacts = await fetchStudyChartFacts(id, current.surgery_start_time);
      } catch (loadError) {
        chartFacts = null;
      }
      if (cancel || ownerRef.current !== id) return;
      const source = { patient: current, characteristics, recommendation, chartFacts };
      sources.current.set(id, source);
      let next = seeded;
      try {
        next = stampPatientDemographics(prefillCompassChart(source), current, 'compass');
      } catch (loadError) {
        next = stampPatientDemographics(seeded, current, 'compass');
      }
      setState(adoptStudy(
        patientChartId(true, id),
        'compass',
        overlaySavedAnswers(next, savedState(true, id) || saved),
      ));
      setChartNote(chartIsReady(current, characteristics, chartFacts) ? 'ready' : 'unavailable');
    })();
    return () => {
      cancel = true;
      flushDemoChart(id);
    };
  }, [demo, patient?.id]);

  const packet = useMemo(() => {
    if (!fields) return null;
    return splitCaseFields(fields, state.arm, state.region);
  }, [fields, state.arm, state.region]);

  const outline = useMemo(
    () => (packet ? buildOutline(packet.fields, state.arm) : []),
    [packet, state.arm],
  );

  useEffect(() => {
    if (!demo || !patient?.id || chartNote !== 'ready') return undefined;
    const current = stateRef.current;
    const ready = outline.length > 0 && packet;
    let next = current;
    const enrollFields = ready
      ? (outline.find((item) => item.id === 'enrollment')?.forms.find((item) => item.id === 'enroll')?.fields || [])
      : [];
    const deviceFields = enrollFields.filter((field) => String(field.field_key || '').startsWith('device.'));
    if (ready) {
      next = {
        ...current,
        phases: phaseFlagsForOutline(outline, current.forms, {
          percentFor: (form, visitId, forms) => {
            const saved = forms?.[instanceKey(form.id, visitId)] || {};
            if (form.id === 'criteria') {
              const enroll = forms?.[instanceKey('enroll', '')] || {};
              return criteriaProgress(
                packet.criteria,
                deviceFields,
                enroll.values || {},
                saved.values || {},
                packet.reviewers,
              ).percent;
            }
            return formProgress(form.fields, saved.values || {}, saved.repeats || {}).percent;
          },
        }),
      };
    }
    const completedSections = ready ? completedSubsectionKeys(outline, next.forms) : null;
    if (ready && completedSections) {
      const enrollSaved = next.forms?.[instanceKey('enroll', '')] || {};
      const criteriaSaved = next.forms?.[instanceKey('criteria', '')] || {};
      const criteria = criteriaProgress(
        packet.criteria,
        deviceFields,
        enrollSaved.values || {},
        criteriaSaved.values || {},
        packet.reviewers,
      );
      if (criteria.total > 0 && criteria.filled === criteria.total) {
        completedSections.push(`${instanceKey('criteria', '')}::criteria`);
      }
    }
    persistDemoStudy(patient.id, 'compass', next, completedSections);
    return undefined;
  }, [demo, patient?.id, state, outline, chartNote, packet]);

  const location = findLocation(outline, state.active) || (outline.length ? (() => {
    const next = firstLocation(outline);
    return findLocation(outline, next);
  })() : null);

  useEffect(() => {
    if (!outline.length) return;
    setState((current) => (
      findLocation(outline, current.active)
        ? current
        : { ...current, active: firstLocation(outline) }
    ));
  }, [outline]);

  const formsForNext = () => outlineStops(outline).map((stop) => {
    const formKey = instanceKey(stop.form.id, stop.visitId);
    const saved = state.forms[formKey] || {};
    if (stop.form.id === 'criteria') {
      const enrollFields = outline
        .find((item) => item.id === 'enrollment')
        ?.forms.find((item) => item.id === 'enroll')
        ?.fields || [];
      const enrollSaved = state.forms[instanceKey('enroll', '')] || {};
      return {
        formKey,
        fields: criteriaQuestionEntries(
          packet?.criteria || [],
          enrollFields.filter((item) => String(item.field_key || '').startsWith('device.')),
          enrollSaved.values || {},
          saved.values || {},
          packet?.reviewers || [],
        ),
      };
    }
    return {
      formKey,
      fields: visibleFieldEntries(stop.form.fields, saved.values || {}, saved.repeats || {}),
    };
  });

  const { select, goToNextMissing, toggleLock, focusFieldKey, following, locked } = useWhatsNext({
    outline,
    state,
    setState,
    formsFor: formsForNext,
    resumeKey: chartNote === 'ready' && outline.length ? (patient?.id || '') : '',
  });

  const updateValue = (key, fieldKey, value) => {
    let offer = null;
    setState((current) => {
      const form = current.forms[key] || { values: {}, repeats: {} };
      const stop = outlineStops(outline).find((item) => instanceKey(item.form.id, item.visitId) === key);
      const prior = form.values || {};
      if (
        stop?.form.id === 'consent'
        && isFirstFormDate(fieldKey, value, stop.form.fields, prior, form.repeats)
        && !dateDeclinedRef.current
      ) {
        offer = { formKey: key, date: value };
      }
      const edited = {
        ...current,
        forms: {
          ...current.forms,
          [key]: { ...form, values: { ...form.values, [fieldKey]: value } },
        },
      };
      return commitLinkedEdit(chartIdRef.current, 'compass', edited, fieldKey, key);
    });
    if (offer) setDateOffer(offer);
  };

  const applyOfferedDate = () => {
    if (!dateOffer) return;
    const offer = dateOffer;
    setDateOffer(null);
    dateDeclinedRef.current = true;
    setState((current) => {
      const form = current.forms[offer.formKey] || { values: {}, repeats: {} };
      const stop = outlineStops(outline).find((item) => instanceKey(item.form.id, item.visitId) === offer.formKey);
      const nextValues = applySharedFormDate(stop?.form.fields, form.values, form.repeats, offer.date);
      const edited = {
        ...current,
        forms: {
          ...current.forms,
          [offer.formKey]: { ...form, values: nextValues },
        },
      };
      return commitLinkedEdit(chartIdRef.current, 'compass', edited, '', offer.formKey);
    });
  };

  const dismissOfferedDate = () => {
    dateDeclinedRef.current = true;
    setDateOffer(null);
  };

  const updateRepeats = (key, groupId, count) => {
    setState((current) => {
      const form = current.forms[key] || { values: {}, repeats: {} };
      return {
        ...current,
        forms: {
          ...current.forms,
          [key]: { ...form, repeats: { ...form.repeats, [groupId]: count } },
        },
      };
    });
  };

  const roster = useMemo(() => {
    const list = Array.isArray(patients) ? patients.filter((row) => row?.id) : [];
    const unique = [];
    const seen = new Set();
    list.forEach((row) => {
      if (seen.has(row.id)) return;
      seen.add(row.id);
      unique.push(row);
    });
    if (patient?.id && !seen.has(patient.id)) unique.unshift(patient);
    return unique;
  }, [patients, patient]);

  const showChart = !demo || (Boolean(patient?.id) && chartNote !== 'pick');
  const patientName = patient?.name || 'This patient';
  const storedAnswers = demoChartsUseDatabase()
    ? 'Answers are saved for this patient.'
    : 'Answers stay on this page.';
  let note = 'Example chart. Answers stay in this browser and are not written to the database.';
  if (demo && chartNote === 'pick') {
    note = `Choose a patient. The chart fills from Phoenix and the recommendation. ${storedAnswers}`;
  } else if (demo && chartNote === 'loading') {
    note = `${patientName}. Reading Phoenix and the recommendation…`;
  } else if (demo && chartNote === 'unavailable') {
    note = `${patientName}. Chart details are unavailable, so the forms are empty. ${storedAnswers}`;
  } else if (demo) {
    note = `${patientName}. Filled from Phoenix and the recommendation. ${storedAnswers}`;
  }

  const refill = () => {
    const id = patient?.id;
    const source = id ? sources.current.get(id) : null;
    if (!source) return;
    const next = broadcastStudy(patientChartId(true, id), 'compass', prefillCompassChart(source));
    ownerRef.current = id;
    setState(next);
    setChartNote(chartIsReady(source.patient, source.characteristics, source.chartFacts) ? 'ready' : 'unavailable');
  };

  if (error) return <div className="compass-error">{error}</div>;
  const formLoading = !fields || !packet || !location || (demo && Boolean(patient?.id) && chartNote === 'loading');
  if (formLoading) {
    return (
      <div className={`compass-workspace${embedded ? ' is-embedded' : ''}`}>
        {!embedded && (
        <header className="compass-top">
          <div>
            <h1 className="compass-title">COMPASS</h1>
            {demo && chartNote === 'pick' && (
            <PatientSelect roster={roster} patient={patient} onSelectPatient={onSelectPatient} />
            )}
            {chartNote !== 'loading' && <p className="compass-note">{note}</p>}
          </div>
        </header>
        )}
        <LoadingSpinner />
      </div>
    );
  }

  const { phase, visit, form } = location;
  const visitId = visit?.id || phase.visitId || '';
  const key = instanceKey(form.id, visitId);
  const values = formValues(state, key);
  const enrollKey = instanceKey('enroll', '');
  const enrollForm = outline
    .find((item) => item.id === 'enrollment')
    ?.forms.find((item) => item.id === 'enroll');
  const deviceFields = (enrollForm?.fields || []).filter((item) => item.field_key.startsWith('device.'));
  const showVisitMeta = phase.id === 'preop' || phase.id === 'followup';

  const percentFor = (itemForm, formVisitId) => {
    const saved = state.forms[instanceKey(itemForm.id, formVisitId)] || {};
    if (itemForm.id === 'criteria') {
      return criteriaProgress(
        packet.criteria,
        deviceFields,
        formValues(state, enrollKey),
        saved.values || {},
        packet.reviewers,
      ).percent;
    }
    return formProgress(itemForm.fields, saved.values || {}, saved.repeats || {}).percent;
  };

  const groupDone = (groupKey) => {
    const [phaseId, visitGroupId] = groupKey.split(':');
    const phaseItem = outline.find((item) => item.id === phaseId);
    if (!phaseItem) return false;
    if (visitGroupId) {
      const itemVisit = phaseItem.visits?.find((item) => item.id === visitGroupId);
      return Boolean(itemVisit?.forms.every((itemForm) => percentFor(itemForm, itemVisit.id) === 100));
    }
    if (phaseItem.visits) {
      return phaseItem.visits.every((itemVisit) => (
        itemVisit.forms.every((itemForm) => percentFor(itemForm, itemVisit.id) === 100)
      ));
    }
    const formVisitId = phaseItem.visitId || '';
    return phaseItem.forms.every((itemForm) => percentFor(itemForm, formVisitId) === 100);
  };

  const staleGroups = Object.keys(revealed).filter((groupKey) => revealed[groupKey] && !groupDone(groupKey));
  if (staleGroups.length) {
    setRevealed((current) => {
      const next = { ...current };
      let changed = false;
      staleGroups.forEach((groupKey) => {
        if (next[groupKey]) {
          next[groupKey] = false;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }

  const toggleGroup = (groupKey) => {
    if (!groupDone(groupKey)) return;
    setRevealed((current) => ({ ...current, [groupKey]: !current[groupKey] }));
  };

  return (
    <div className={`compass-workspace${embedded ? ' is-embedded' : ''}`}>
      <header className="compass-top">
        {!embedded && (
        <div>
          <h1 className="compass-title">COMPASS</h1>
          {demo && (
            <PatientSelect roster={roster} patient={patient} onSelectPatient={onSelectPatient} />
          )}
          <p className="compass-note">{note}</p>
        </div>
        )}
        {showChart && (
        <div className="compass-controls" role="group" aria-label="COMPASS arm">
          {ARMS.map((arm) => (
            <button
              key={arm.id}
              type="button"
              aria-pressed={state.arm === arm.id}
              onClick={() => setState((current) => (
                commitLinkedEdit(chartIdRef.current, 'compass', { ...current, arm: arm.id }, 'arm', null)
              ))}
            >
              {arm.label}
            </button>
          ))}
          {demo ? (
            <button type="button" className="compass-text-button" onClick={refill}>
              Refill from the chart
            </button>
          ) : (
            <button
              type="button"
              className="compass-text-button"
              onClick={() => {
                clearExampleChart();
                setState(emptyState());
              }}
            >
              Reset example
            </button>
          )}
        </div>
        )}
      </header>

      {showChart && state.arm === 'Adult Spinal Deformity' && (
        <div className="compass-controls" style={{ padding: '0 0.35rem 0.7rem' }} role="group" aria-label="Deformity region">
          {REGIONS.map((region) => (
            <button
              key={region.id}
              type="button"
              aria-pressed={state.region === region.id}
              onClick={() => setState((current) => (
                commitLinkedEdit(chartIdRef.current, 'compass', { ...current, region: region.id }, 'region', null)
              ))}
            >
              {region.label}
            </button>
          ))}
        </div>
      )}

      {showChart && state.arm === 'Adult Spinal Deformity' && !state.region && (
        <div className="compass-banner">
          Choose the treated region to open the cervical or thoracolumbar exams and the matching questionnaires.
        </div>
      )}

      {showChart && (
      <div className="compass-shell">
        <nav className="compass-rail" aria-label="COMPASS forms">
          {outline.map((item) => (
            <RailCategory
              key={item.id}
              label={item.label}
              complete={groupDone(item.id)}
              collapsed={groupDone(item.id) && !revealed[item.id]}
              onToggle={() => toggleGroup(item.id)}
            >
              {item.visits ? item.visits.map((itemVisit) => {
                const visitKey = `${item.id}:${itemVisit.id}`;
                const visitDone = groupDone(visitKey);
                return (
                  <RailCategory
                    key={itemVisit.id}
                    label={itemVisit.label}
                    visit
                    complete={visitDone}
                    collapsed={visitDone && !revealed[visitKey]}
                    onToggle={() => toggleGroup(visitKey)}
                  >
                    {itemVisit.forms.map((itemForm) => (
                      <RailButton
                        key={itemForm.id}
                        label={itemForm.title}
                        active={phase.id === item.id && visit?.id === itemVisit.id && form.id === itemForm.id}
                        percent={percentFor(itemForm, itemVisit.id)}
                        onClick={() => select(item, itemVisit, itemForm)}
                      />
                    ))}
                  </RailCategory>
                );
              }) : item.forms.map((itemForm) => (
                <RailButton
                  key={itemForm.id}
                  label={itemForm.title}
                  active={phase.id === item.id && form.id === itemForm.id && !visit}
                  percent={percentFor(itemForm, item.visitId || '')}
                  onClick={() => select(item, null, itemForm)}
                />
              ))}
            </RailCategory>
          ))}
        </nav>

        <main className="compass-main" onClick={onEngage}>
          <FormNavbar
            phase={phase.label}
            title={form.title}
            onWhatsNext={goToNextMissing}
            missingOn={following}
            missingLocked={locked}
            onToggleMissingLock={toggleLock}
          />

          {showVisitMeta && (
            <div className="compass-visit-date">
              <div className="compass-field">
                <label htmlFor="compass-visit-date">Visit date</label>
                <input
                  id="compass-visit-date"
                  className="form-control"
                  type="date"
                  value={state.visits[visitId]?.date || ''}
                  onChange={(event) => {
                    const date = event.target.value;
                    setState((current) => ({
                      ...current,
                      visits: { ...current.visits, [visitId]: { ...current.visits[visitId], date } },
                    }));
                  }}
                />
              </div>
              {visitId === 'unscheduled' && (
                <div className="compass-field">
                  <label htmlFor="compass-unscheduled-reason">Reason for the unscheduled visit</label>
                  <input
                    id="compass-unscheduled-reason"
                    className="form-control"
                    type="text"
                    value={state.visits[visitId]?.reason || ''}
                    onChange={(event) => {
                      const reason = event.target.value;
                      setState((current) => ({
                        ...current,
                        visits: { ...current.visits, [visitId]: { ...current.visits[visitId], reason } },
                      }));
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {form.id === 'criteria' ? (
            <CriteriaPanel
              criteria={packet.criteria}
              deviceFields={deviceFields}
              deviceValues={formValues(state, enrollKey)}
              values={values}
              onChange={(fieldKey, value) => updateValue(key, fieldKey, value)}
              reviewerFields={packet.reviewers}
              onEditDevices={() => {
                const enrollPhase = outline.find((item) => item.id === 'enrollment');
                const target = enrollPhase?.forms.find((item) => item.id === 'enroll');
                if (enrollPhase && target) select(enrollPhase, null, target);
              }}
              idPrefix={key}
              formTitle={form.title}
              highlightFieldKey={focusFieldKey}
            />
          ) : (
            <CompassForm
              fields={form.fields.filter((item) => item.field_key !== 'criteria-placeholder')}
              values={values}
              onChange={(fieldKey, value) => updateValue(key, fieldKey, value)}
              repeatState={state.forms[key]?.repeats || {}}
              onRepeatCount={(groupId, count) => updateRepeats(key, groupId, count)}
              idPrefix={key}
              highlightFieldKey={focusFieldKey}
              formTitle={form.title}
            />
          )}
        </main>
      </div>
      )}
      <Modal show={Boolean(dateOffer)} onHide={dismissOfferedDate} centered animation={false}>
        <Modal.Body>
          <p className="mb-0">Use {dateOffer?.date} for all consent fields?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={dismissOfferedDate}>No</Button>
          <Button variant="primary" onClick={applyOfferedDate}>Yes</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function PatientSelect({ roster, patient, onSelectPatient }) {
  return (
    <label className="compass-patient-label" htmlFor="compass-patient">
      Patient
      <select
        id="compass-patient"
        className="form-select form-select-sm compass-patient-select"
        value={patient?.id || ''}
        onChange={(event) => {
          flushOpenDemoCharts();
          const row = roster.find((item) => item.id === event.target.value);
          if (row && onSelectPatient) onSelectPatient(row);
        }}
      >
        <option value="">Select a patient</option>
        {roster.map((row) => (
          <option key={row.id} value={row.id}>{row.name || row.id}</option>
        ))}
      </select>
    </label>
  );
}

function RailCategory({ label, complete, collapsed, onToggle, visit = false, children }) {
  const headingClass = `compass-rail-heading${visit ? ' is-visit' : ''}${collapsed ? ' is-collapsed' : ''}`;
  return (
    <div className={`compass-rail-group${collapsed ? ' is-collapsed' : ''}${visit ? ' is-visit' : ''}`}>
      {complete ? (
        <button
          type="button"
          className={headingClass}
          aria-expanded={!collapsed}
          aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
          title={label}
          onClick={onToggle}
        >
          <span className="compass-rail-label">{label}</span>
          <small>100%</small>
        </button>
      ) : (
        <div className={headingClass}>
          <span className="compass-rail-label">{label}</span>
        </div>
      )}
      <div className="compass-rail-children" aria-hidden={collapsed || undefined}>
        <div className="compass-rail-children-inner" inert={collapsed ? true : undefined}>
          {children}
        </div>
      </div>
    </div>
  );
}

function RailButton({ label, active, percent, onClick }) {
  return (
    <button
      type="button"
      className={`compass-nav-button compass-rail-button${active ? ' is-active' : ''}`}
      style={{ '--progress': `${percent}%` }}
      onClick={onClick}
    >
      <span className="compass-rail-label">{label}</span>
      <small>{percent}%</small>
    </button>
  );
}
